import { useCallback, useState } from 'react'
import {
  Mail,
  Plus,
  RefreshCw,
  FileText,
  Shield,
  Eye,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Trash2,
} from 'lucide-react'
import {
  useGetOAuthUrl,
  useInboxes,
  useSubscription,
  useUpdateInbox,
  useDeleteInbox,
  type EmailAccount,
} from '@/api'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Link, useBlocker } from '@tanstack/react-router'
import {
  ClassificationPanel,
  type MailboxSection,
} from '@/components/classification/classification-panel'
import { MailboxProviderIcon } from '@/components/inbox/provider-icon'
import { TurnOnDialog } from '@/components/inbox/turn-on-dialog'
import { useSetupRuns } from '@/api/setup-runs'
import { getAccountTypeDisplayName } from '@/lib/enum-display'
import { parseUTCDate } from '@/lib/date-utils'

// Matches missing_permissions() in backend/app/services/classification_mailbox.py.
const permissionNames: Partial<Record<string, string>> = {
  reading: 'reading email',
  sorting: 'sorting',
  categories: 'categories',
}

function listNames(names: string[]) {
  return names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

// Breaks only at the @, so "maxwellweinzierl@gmail.com" never splits mid-word.
function Address({ value }: { value: string }) {
  const at = value.lastIndexOf('@')
  if (at <= 0) return <span className="block truncate">{value}</span>
  return (
    <>
      <span className="inline-block max-w-full truncate align-bottom">
        {value.slice(0, at)}
      </span>
      <wbr />
      <span className="inline-block max-w-full truncate align-bottom">
        {value.slice(at)}
      </span>
    </>
  )
}

function ReconnectNotice({
  inbox,
  onReconnect,
}: {
  inbox: EmailAccount
  onReconnect: () => void
}) {
  const [openedAt] = useState(Date.now)
  const expiry = inbox.email_extension?.ms_subscription_expiry
  const microsoft = inbox.account_type === 'microsoft_email'
  const lostAccess =
    inbox.email_extension?.subscription_status === 'error' ||
    (microsoft &&
      inbox.email_extension?.subscription_status === 'active' &&
      expiry != null &&
      parseUTCDate(expiry).getTime() <= openedAt)
  const missing = (inbox.missing_permissions ?? []).flatMap(
    (key) => permissionNames[key] ?? [],
  )
  if (!lostAccess && !missing.length) return null
  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${lostAccess ? 'border-destructive/50' : 'border-primary/40 bg-primary/5'}`}
    >
      <p className="text-sm">
        {lostAccess
          ? 'Xero Inbox lost access to this inbox. Reconnect it to keep it working.'
          : `Reconnect to allow ${listNames(missing)}`}
      </p>
      <Button variant="outline" onClick={onReconnect}>
        <RefreshCw className="size-4" />
        {microsoft ? 'Reconnect Microsoft' : 'Reconnect Google'}
      </Button>
    </div>
  )
}

export function InboxesPage() {
  const inboxes = useInboxes()
  const subscription = useSubscription()
  const oauth = useGetOAuthUrl()
  const [connectOpen, setConnectOpen] = useState(false)
  const [oauthError, setOAuthError] = useState('')
  const [dirtyMailboxes, setDirtyMailboxes] = useState<Record<string, boolean>>(
    {},
  )
  const onDirtyChange = useCallback(
    (id: string, dirty: boolean) =>
      setDirtyMailboxes((current) =>
        current[id] === dirty ? current : { ...current, [id]: dirty },
      ),
    [],
  )
  const dirty = Object.values(dirtyMailboxes).some(Boolean)
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: dirty,
    withResolver: true,
  })
  const connect = async (provider: 'google' | 'microsoft') => {
    setOAuthError('')
    try {
      const result = await oauth.mutateAsync({
        provider,
        returnTo: '/inboxes',
      })
      window.location.href = result.url
    } catch {
      setOAuthError('The connection could not start. Try again.')
    }
  }
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {blocker.status === 'blocked' && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) blocker.reset()
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Unsaved mailbox changes</DialogTitle>
              <DialogDescription>
                You have unsaved mailbox changes. Save them before leaving, or
                discard them.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => blocker.reset()}>Keep editing</Button>
              <Button variant="outline" onClick={() => blocker.proceed()}>
                Discard and leave
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">
            Inboxes
          </h1>
          <p className="mt-1 text-muted-foreground">
            What Xero Inbox does in each inbox.
          </p>
        </div>
        <Button variant="outline" onClick={() => setConnectOpen(true)}>
          <Plus className="size-4" />
          Connect inbox
        </Button>
      </div>
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect an email account</DialogTitle>
            <DialogDescription>Choose your email provider.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Button
              variant="outline"
              disabled={oauth.isPending}
              onClick={() => connect('google')}
            >
              Connect Google / Gmail
            </Button>
            <Button
              variant="outline"
              disabled={oauth.isPending}
              onClick={() => connect('microsoft')}
            >
              Connect Microsoft / Outlook
            </Button>
            {oauthError && (
              <p role="alert" className="text-sm text-destructive">
                {oauthError}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {inboxes.isLoading ? (
        <div role="status" aria-label="Loading inboxes" className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      ) : inboxes.isError ? (
        <div role="alert" className="space-y-3">
          <p>Your inboxes could not load.</p>
          <Button variant="outline" onClick={() => inboxes.refetch()}>
            Retry inboxes
          </Button>
        </div>
      ) : !inboxes.data?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <Mail className="size-8 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Connect your first inbox</h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              Connect a mailbox to set your instructions and permissions.
            </p>
            <Button onClick={() => setConnectOpen(true)}>
              Choose provider
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {oauthError && (
            <p role="alert" className="text-sm text-destructive">
              {oauthError}
            </p>
          )}
          {subscription.data &&
            (!subscription.data.has_subscription ||
              subscription.data.subscription?.status === 'PAST_DUE') && (
              <div
                role="status"
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
              >
                <p className="text-sm">
                  {subscription.data.has_subscription
                    ? 'Update your payment details to resume processing.'
                    : 'Choose a subscription before enabling automatic processing.'}
                </p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/pricing">View plans</Link>
                </Button>
              </div>
            )}
          <div className="space-y-4">
            {[...inboxes.data]
              .sort((a, b) => a.display_name.localeCompare(b.display_name))
              .map((inbox) => (
                <MailboxCard
                  key={inbox.uuid}
                  inbox={inbox}
                  onDirtyChange={onDirtyChange}
                  onReconnect={() =>
                    connect(
                      inbox.account_type === 'microsoft_email'
                        ? 'microsoft'
                        : 'google',
                    )
                  }
                  reconnecting={oauth.isPending}
                />
              ))}
          </div>
        </>
      )}
    </div>
  )
}

function MailboxCard({
  inbox,
  onDirtyChange,
  onReconnect,
  reconnecting,
}: {
  inbox: EmailAccount
  onDirtyChange: (id: string, dirty: boolean) => void
  onReconnect: () => void
  reconnecting: boolean
}) {
  const [section, setSection] = useState<MailboxSection | null>(null)
  const [visited, setVisited] = useState(false)
  const pause = useUpdateInbox()
  const remove = useDeleteInbox()
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [turningOn, setTurningOn] = useState(false)
  const lastRun = useSetupRuns(inbox.uuid).data?.[0]
  const [disconnectError, setDisconnectError] = useState('')
  const [dirty, setDirty] = useState(false)
  const dirtyChanged = useCallback(
    (id: string, value: boolean) => {
      setDirty(value)
      onDirtyChange(id, value)
    },
    [onDirtyChange],
  )
  const open = (next: MailboxSection) => {
    setVisited(true)
    setSection((current) => (current === next ? null : next))
  }
  return (
    <Card
      role="region"
      aria-label={inbox.display_name}
      className="overflow-hidden"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 px-4 sm:gap-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted sm:size-12">
            <MailboxProviderIcon
              provider={inbox.account_type}
              className="size-6"
            />
          </div>
          <div className="min-w-0">
            <CardTitle
              role="heading"
              aria-level={2}
              aria-label={inbox.display_name}
              title={inbox.display_name}
              className="text-base leading-snug"
            >
              <Address value={inbox.display_name} />
            </CardTitle>
            <CardDescription className="mt-1 flex flex-wrap items-center gap-2">
              {getAccountTypeDisplayName(inbox.account_type)}
              <Badge variant={inbox.is_active ? 'default' : 'secondary'}>
                {inbox.is_active ? 'Active' : 'Paused'}
              </Badge>
            </CardDescription>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <Switch
            aria-label={`Processing for ${inbox.display_name}`}
            checked={inbox.is_active}
            disabled={pause.isPending}
            onCheckedChange={(checked) =>
              checked
                ? setTurningOn(true)
                : pause.mutate({
                    inboxId: inbox.uuid,
                    data: { is_active: false },
                  })
            }
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Options for ${inbox.display_name}`}
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={reconnecting} onClick={onReconnect}>
                <RefreshCw className="mr-2 size-4" />
                Reconnect
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => {
                  setDisconnectError('')
                  setConfirmDisconnect(true)
                }}
              >
                <Trash2 className="mr-2 size-4" />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-4 sm:px-6">
        <ReconnectNotice inbox={inbox} onReconnect={onReconnect} />
        <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center">
          {(
            [
              ['instructions', 'Instructions', FileText],
              ['permissions', 'Actions', Shield],
              ['preview', 'Preview', Eye],
            ] as const
          ).map(([value, label, Icon]) => (
            <Button
              key={value}
              size="sm"
              variant={section === value ? 'secondary' : 'outline'}
              aria-expanded={section === value}
              onClick={() => open(value)}
              className="px-2 sm:px-3"
            >
              <Icon className="hidden size-4 sm:block" />
              {label}
              {section === value ? (
                <ChevronUp className="hidden size-3 sm:block" />
              ) : (
                <ChevronDown className="hidden size-3 sm:block" />
              )}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-primary">
          <Link to="/events" search={{ inbox: inbox.uuid }} className="hover:underline">
            See events for this inbox
          </Link>
          {lastRun && (
            <Link
              to="/runs/$runId"
              params={{ runId: lastRun.id }}
              className="hover:underline"
            >
              Last run
            </Link>
          )}
          <Link to="/setup" search={{ inbox: inbox.uuid }} className="hover:underline">
            Ask about this inbox
          </Link>
        </div>
        {pause.isError && (
          <p role="alert" className="text-sm text-destructive">
            Processing could not pause. Try again.
          </p>
        )}
        {visited && (
          <div hidden={section === null} className="border-t pt-4">
            <ClassificationPanel
              inboxId={inbox.uuid}
              gmail={inbox.account_type === 'google_email'}
              section={section ?? 'instructions'}
              onSectionChange={setSection}
              onDirtyChange={dirtyChanged}
            />
          </div>
        )}
      </CardContent>
      {turningOn && (
        <TurnOnDialog
          inboxId={inbox.uuid}
          inboxName={inbox.display_name}
          unsaved={dirty}
          onClose={() => setTurningOn(false)}
          onSetUpActions={() => {
            setTurningOn(false)
            setVisited(true)
            setSection('permissions')
          }}
        />
      )}
      <Dialog
        open={confirmDisconnect}
        onOpenChange={(open) => {
          if (!remove.isPending) setConfirmDisconnect(open)
        }}
      >
        <DialogContent className="sentry-block ph-no-capture">
          <DialogHeader>
            <DialogTitle>Disconnect inbox?</DialogTitle>
            <DialogDescription>
              {inbox.display_name}
              {dirty ? ' — unsaved edits will be discarded.' : ''}
            </DialogDescription>
          </DialogHeader>
          {disconnectError && (
            <p role="alert" className="text-sm text-destructive">
              {disconnectError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={remove.isPending}
              onClick={() => setConfirmDisconnect(false)}
            >
              Keep inbox
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={async () => {
                setDisconnectError('')
                try {
                  await remove.mutateAsync(inbox.uuid)
                  onDirtyChange(inbox.uuid, false)
                  setConfirmDisconnect(false)
                } catch {
                  setDisconnectError(
                    'This inbox could not be disconnected. Refresh your inboxes and try again.',
                  )
                }
              }}
            >
              {remove.isPending
                ? 'Disconnecting…'
                : dirty
                  ? 'Discard edits and disconnect'
                  : 'Disconnect inbox'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
